import React, {useEffect, useState, useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Carousel from 'react-multi-carousel';
import {themeGet} from '@styled-system/theme-get';
import API from '../../network/dashboardAPI';
import {useIntersectionObserver} from '../../hooks/useIntersectionObserver';
import {ArrowNext, ArrowPrev} from '../AllSvgIcon';
import {CarouselImage} from '../Carousel/Carousel.style';
import delishoplogo from '../../assets/icons/delishop_logo2.svg';

/**
 * Styled Components
 */
const ButtonPrev = styled('button')`
  height: 40px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
    background-color: rgba(255, 255, 255, 0.2);
    color: ${themeGet('colors.primary', '#9cc54b')};
  padding: 0;
  border-radius: 20px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  border: 0;
  outline: 0;
  cursor: pointer;
  position: absolute;
  top: 50%;
  left: 40px;
  margin-top: -20px;
  z-index: 99;

  @media (max-width: 990px) {
    display: none;
  }
`;

const ButtonNext = styled('button')`
  height: 40px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
    background-color: rgba(255, 255, 255, 0.2);
    color: ${themeGet('colors.primary', '#9cc54b')};
  padding: 0;
  border-radius: 20px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  border: 0;
  outline: 0;
  cursor: pointer;
  position: absolute;
  top: 50%;
  right: 40px;
  margin-top: -20px;
  z-index: 99;

  @media (max-width: 990px) {
    display: none;
  }
`;

const ButtonGroupWrapper = styled('div')``;

const VideoPlaceholder = styled('div')`
    width: 100%;
    max-height: calc(100vh - 200px);
    min-height: 400px;
    background-color: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (max-width: 768px) {
        min-height: 300px;
    }
`;

const LoadingSpinner = styled('div')`
    border: 4px solid #f3f3f3;
    border-top: 4px solid ${themeGet('colors.primary', '#9cc54b')};
    border-radius: 50%;
    width: 50px;
    height: 50px;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

/**
 * Carousel Navigation Buttons
 */
const PrevButton = ({onClick}) => (
	<ButtonPrev onClick={(e) => {
		e.preventDefault();
		onClick();
	}} className="prevButton">
		<ArrowPrev/>
	</ButtonPrev>
);

const NextButton = ({onClick}) => (
	<ButtonNext onClick={(e) => {
		e.preventDefault();
		onClick();
	}} className="nextButton">
		<ArrowNext/>
	</ButtonNext>
);

const ButtonGroup = ({next, previous}) => (
	<ButtonGroupWrapper>
		<PrevButton onClick={previous}/>
		<NextButton onClick={next}/>
	</ButtonGroupWrapper>
);

/**
 * Carousel Responsive Configuration
 */
const CAROUSEL_RESPONSIVE = {
	desktop: {
		breakpoint: {max: 3000, min: 768},
		items: 1,
		slidesToSlide: 1,
	},
	tablet: {
		breakpoint: {max: 768, min: 464},
		items: 1,
		slidesToSlide: 1,
	},
	mobile: {
		breakpoint: {max: 464, min: 0},
		items: 1,
		slidesToSlide: 1,
	},
};

/**
 * Skeleton/Placeholder Component
 */
const VideoSkeleton = () => (
	<div style={{padding: '0px', overflow: 'hidden', position: 'relative'}}>
		<a href="#" style={{display: 'flex', cursor: 'pointer'}} onClick={(e) => e.preventDefault()}>
			<CarouselImage
				src={delishoplogo}
				alt="logo"
				onError={(e) => {
					e.target.onerror = null;
					e.target.src = delishoplogo;
				}}
			/>
			<div>
				<div style={{color: 'white', fontSize: 22, fontWeight: 'bold'}}></div>
				<div style={{color: 'white', fontSize: 12}}></div>
			</div>
		</a>
	</div>
);

/**
 * HomePageVideo Component
 * Optimized with lazy loading - only loads when visible in viewport
 * Auto-plays when scrolled into view
 */
const HomePageVideo = ({deviceType}) => {
	const [dataList, setDataList] = useState([]);
	const [loading, setLoading] = useState(false);
	const videoRefs = React.useRef([]);

	// Lazy load: only load data when component becomes visible
	const {ref, isVisible} = useIntersectionObserver({
		threshold: 0,
		rootMargin: '200px', // Start loading 200px before component is visible
		triggerOnce: true,   // Only load once
	});

	// Fetch video data
	const getHomePageVideo = useCallback(async () => {
		setLoading(true);
		try {
			const response = await API.getAllVideoHomePage({get_type: 1});
			const data = await response.json();

			if (data.success === true) {
				setDataList(data.data || []);
			}
		} catch (error) {
			console.error('Error fetching homepage videos:', error);
		} finally {
			setLoading(false);
		}
	}, []);

	// Only fetch when component becomes visible
	useEffect(() => {
		if (isVisible && dataList.length === 0) {
			getHomePageVideo();
		}
	}, [isVisible, dataList.length, getHomePageVideo]);

	// Auto-play video when it scrolls into view
	useEffect(() => {
		if (!dataList.length || !isVisible) return;

		// Check if IntersectionObserver is supported (Safari 12.1+, all modern browsers)
		if (!window.IntersectionObserver) {
			return;
		}

		const observers = videoRefs.current.map((videoElement, index) => {
			if (!videoElement) return null;

			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							// Ensure video is muted before playing
							videoElement.muted = true;

							// Detect Safari browser (stricter auto-play policies)
							const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
							const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

							// Play video when in view
							const playPromise = videoElement.play();

							if (playPromise !== undefined) {
								playPromise
									.then(() => {
										// Video auto-play started successfully
									})
									.catch((error) => {
										// Safari/iOS: Auto-play is blocked, keep muted and show play button overlay
										if (isSafari || isIOS) {
											// Keep video muted for potential user interaction
											videoElement.muted = true;
											// Add a visual indicator that user needs to click
											videoElement.setAttribute('data-autoplay-blocked', 'true');
										} else {
											// Other browsers: unmute so user can play with sound
											videoElement.muted = false;
										}
									});
							}
						} else {
							// Pause video when out of view
							if (!videoElement.paused) {
								videoElement.pause();
							}
						}
					});
				},
				{
					threshold: 0.3, // Play when 30% of video is visible (easier to trigger)
					rootMargin: '50px', // Start playing slightly before fully in view
				}
			);

			observer.observe(videoElement);
			return observer;
		});

		return () => {
			observers.forEach((observer) => {
				if (observer) observer.disconnect();
			});
		};
	}, [dataList, isVisible]);

	// Render video items
	const renderVideos = useMemo(() => {
		if (loading) {
			return (
				<VideoPlaceholder>
					<LoadingSpinner/>
				</VideoPlaceholder>
			);
		}

		if (dataList.length > 0) {
			return dataList.map((item, index) => (
				<video
					key={`video-${item.id || index}`}
					ref={(el) => (videoRefs.current[index] = el)}
					className="videoTag m-auto"
					width="100%"
					height="100%"
					controls
					muted // Muted to allow auto-play (browsers require this)
					playsInline // For mobile devices
					preload="metadata" // SEO: Only preload metadata, not full video (better bandwidth)
					loop // Loop the video
					aria-label={item.title || 'Homepage promotional video'} // SEO: Accessibility
					itemProp="video" // SEO: Schema.org markup
					style={{maxHeight: 'calc(100vh - 200px)', backgroundColor: 'black'}}
					onLoadedMetadata={(e) => {
						// When video metadata is loaded, try to play if in view
						if (e.target.getBoundingClientRect().top < window.innerHeight) {
							e.target.play().catch(() => {
								// Auto-play on load failed - will rely on IntersectionObserver
							});
						}
					}}
				>
					<source src={item.video_link} type="video/mp4"/>
					{/* SEO: Add text fallback for search engines and accessibility */}
					<p>
						Your browser does not support the video tag.
						{item.title && ` Watch our ${item.title} video.`}
					</p>
				</video>
			));
		}

		// Skeleton fallback
		return [0, 1].map((elem) => <VideoSkeleton key={`skeleton-${elem}`}/>);
	}, [dataList, loading]);

	// Don't render until visible (performance optimization)
	if (!isVisible) {
    return (
	    <VideoPlaceholder ref={ref}>
		    <LoadingSpinner/>
	    </VideoPlaceholder>
    );
	}

	return (
		<div ref={ref}>
			<Carousel
				arrows={true}
				responsive={CAROUSEL_RESPONSIVE}
				ssr={true}
				infinite={dataList.length > 1}
				containerClass="container-with-dots"
				deviceType={deviceType}
				autoPlay={true}
				renderButtonGroupOutside={true}
				additionalTransfrom={0}
				customButtonGroup={<ButtonGroup/>}
			>
				{renderVideos}
			</Carousel>
		</div>
	);
};

HomePageVideo.propTypes = {
	deviceType: PropTypes.string,
};

HomePageVideo.defaultProps = {
	deviceType: 'desktop',
};

export default React.memo(HomePageVideo);
